import { useState } from "react";
import { clerkEnabled } from "@/lib/clerk";
import { Suspense, lazy } from "react";
// Lazy for the same reason as ClerkGate: no key, no SDK — not in the bundle
// and not resolved by Jest.
const ClerkAuthPanel = lazy(() => import("@/components/auth/ClerkAuthPanel"));
import Stage from "@/components/landing/Stage";
import Logomark from "@/components/Logomark";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/notices";
import { ArrowRight } from "lucide-react";
import NoticeBar from "@/components/NoticeBar";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // AFTER every hook, deliberately. An early return above them makes the
  // hooks conditional — React requires the same hooks in the same order on
  // every render, and eslint fails the build for it. The legacy state is
  // simply unused when Clerk is on, which costs nothing.
  if (clerkEnabled()) {
    return (
      <Suspense fallback={null}>
        <ClerkAuthPanel mode="sign-in" />
      </Suspense>
    );
  }

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      {/* Left brand panel */}
      <Stage phase={0.15} className="hidden lg:block" contentClassName="h-full flex flex-col justify-between p-12">
        <div>
          <Link to="/" className="flex items-center gap-2" data-testid="login-logo">
            <Logomark />
            <span className="font-heading font-bold tracking-tight text-lg">NextOpp</span>
          </Link>
        </div>
        <div>
          <h2 className="font-heading text-4xl xl:text-5xl font-bold tracking-tighter leading-[1] max-w-md">
            Welcome back. Let’s <em className="font-display not-italic text-dawn-300">tailor</em> your next application.
          </h2>
          <p className="mt-6 text-white/70 max-w-md">Your resumes, jobs, and match scores — all where you left them.</p>
        </div>
        <div className="u-label text-white/45">AI job command center</div>
      </Stage>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <Logomark />
              <span className="font-heading font-bold tracking-tight text-lg">NextOpp</span>
            </Link>
          </div>
          <NoticeBar className="top-0 mb-6" />
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600 mb-2">SIGN IN</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-stone-950">Welcome back</h1>
          <p className="mt-2 text-sm text-stone-600">Enter your credentials or continue with Google.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-stone-900 mb-1.5 block">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="rounded-sm" data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-stone-900">Password</Label>
                {/* The recovery flow has to be reachable from where people get
                    stuck, which is this form. */}
                <Link to="/forgot-password" className="text-xs text-brand hover:underline" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="rounded-sm" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-brand hover:bg-brand-hover text-white rounded-sm font-medium" data-testid="login-submit-button">
              {loading ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-sand" />
            <span className="text-xs uppercase tracking-[0.2em] text-stone-600 font-bold">or</span>
            <div className="flex-1 h-px bg-sand" />
          </div>

          <button
            onClick={handleGoogle}
            className="w-full h-11 border border-sand rounded-sm hover:bg-paper flex items-center justify-center gap-3 text-sm font-medium transition-colors"
            data-testid="login-google-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </button>

          <div className="mt-8 text-sm text-stone-600 text-center">
            Don’t have an account? <Link to="/signup" className="text-brand font-medium hover:underline" data-testid="login-signup-link">Sign up</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
